import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory } from "@insite/mobius/utilities/HistoryContext";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React,{ FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    description = "description",
    buttonLink = "buttonLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.description]: string;
        [fields.buttonLink]: LinkFieldValue;
    };
}

export interface LogInStyles {
    mainGridContainer: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    logInGridItem?: GridItemProps;
    logInButton?: ButtonPresentationProps;
}

export const logInStyles: LogInStyles = {
    mainGridContainer: {
        gap: 10,
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    descriptionGridItem: {
        width: 12,
        css: css`
            display: flex;
            text-align: left;
        `,
    },
    descriptionText: {
        css: css`
            p {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                line-height: 20px;
                font-weight: ${TYPOGRAPHY.weight.base};
                color: ${COLORS.background.tag};
            }
        `,
    },
    logInGridItem: {
        width: 12,
        css: css`
            display: flex;
            padding-top: 16px !important;
        `,
    },
    logInButton: {
        css: css`
            width: 200px;
            min-height: 32px;
            border-radius: 16px !important;
        `,
    }
};

const mapStateToProps = (state: ApplicationState) => {
    const currentLocation= getLocation(state);
    return {
        currentLocation,
        currentUserIsGuest: getCurrentUserIsGuest(state),
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasHistory;

export const CmsLogIn : FC<Props> = ({ fields, currentLocation, currentUserIsGuest}) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();
    // const styles = useMergeStyles("subscribe", subscribeStyles);
    const styles = logInStyles;

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/" || returnUrl === "/SignIn" || returnUrl === "/MyAccount/CreateAccount" || (currentUserIsGuest && returnUrl.includes("/OrderConfirmation"))) {
                history.push(`${url}`);
            } else {
                history.push(`${url}?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
        }
    };

    return (
        <GridContainer {...styles.mainGridContainer}>
            <GridItem {...styles.descriptionGridItem}>
                <Typography {...styles.descriptionText}>{parse(transformContentHtml(fields.description), parserOptions)}</Typography>
            </GridItem>
            <GridItem {...styles.logInGridItem}>
                <Button {...styles.logInButton} onClick={onClick}  >
                {fields.label || title || url}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(CmsLogIn),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Log In",
                fieldType: "Translatable",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            }
        ],
    },
};

export default widgetModule;
